import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Flex,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { getSystemAxisListById } from 'api/auth.api';
import { useCreateDiseaseProtocol } from '../hooks/useCreateDiseaseProtocol';
import CreatableDropdown from 'components/CreatableDropDown/index';
import { useDiseaseProtocolById } from '../hooks/useDiseaseProtocolById';
import { useUpdateDiseaseProtocol } from '../hooks/useUpdateDiseaseProtocol';

function DiseaseProtocolForm({ axisList, editId, onSuccess, setSelectedId }) {
  const [selectedAxis, setSelectedAxis] = useState('');
  const [selectedAxisHiDisable, setSelectedAxisHiDisable] = useState(true);
  const [localOptions, setLocalOptions] = useState([]);

  const apiOptions = React.useMemo(() => {
    return axisList.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return {
          label: item.system || item.name || JSON.stringify(item),
          value: item.system || item._id || JSON.stringify(item),
          systemHi: item.systemHi || '',
        };
      }
      return {
        label: item,
        value: item,
        systemHi: '',
      };
    });
  }, [axisList]);

  const systemOptions = React.useMemo(() => {
    return [...apiOptions, ...localOptions];
  }, [apiOptions, localOptions]);

  const handleFilterChange = (value) => {
    setSelectedAxis(value || '');

    const selectedOption = systemOptions.find((opt) => opt.value === value);

    setValue('system', value || '', {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (selectedOption?.systemHi) {
      setValue('systemHi', selectedOption.systemHi, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue('systemHi', '', { shouldValidate: true });
    }
  };

  const handleCreateSystem = (newValue) => {
    const exists = systemOptions.some(
      (opt) => opt.value.toLowerCase() === newValue.toLowerCase(),
    );

    if (!exists) {
      const newOption = { label: newValue, value: newValue };
      setLocalOptions((prev) => [...prev, newOption]);
      setSelectedAxis(newValue);
      setSelectedAxisHiDisable(false);
      setValue('system', newValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('systemHi', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const id = editId?._id;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      diseaseEnglish: '',
      diseaseHindi: '',
      system: '',
      systemHi: '',
      bottle1PrimaryFunctional: '',
      bottle1Supportive: '',
      bottle1E: '',
      bottle2PrimaryFunctional: '',
      bottle2Supportive: '',
      bottle2E: '',
      bottle3Chronic: '',
      bottle3Supportive: '',
      bottle3E: '',
      bottle4Degeneration: '',
      bottle4Supportive: '',
      bottle4E: '',
      state: '',
      temperament: '',
      acupuncturePoints: '',
      product: '',
      active: true,
    },
  });

  const { data, isLoading: isIdLoading } = useDiseaseProtocolById(id);

  useEffect(() => {
    if (data?.data?.result) {
      reset({
        system: data.data.result.system,
        systemHi: data.data.result.systemHi,
        diseaseEnglish: data.data.result.diseaseEnglish,
        diseaseHindi: data.data.result.diseaseHindi,
        bottle1PrimaryFunctional: data.data.result.bottle_1_primary_functional,
        bottle1Supportive: data.data.result.bottle_1_supportive,
        bottle1E: data.data.result.bottle_1_E,
        bottle2PrimaryFunctional: data.data.result.bottle_2_primary_functional,
        bottle2Supportive: data.data.result.bottle_2_supportive,
        bottle2E: data.data.result.bottle_2_E,
        bottle3Chronic: data.data.result.bottle_3_chronic,
        bottle3Supportive: data.data.result.bottle_3_supportive,
        bottle3E: data.data.result.bottle_3_E,
        bottle4Degeneration: data.data.result.bottle_4_degeneration,
        bottle4Supportive: data.data.result.bottle_4_supportive,
        bottle4E: data.data.result.bottle_4_E,
        state: data.data.result.state,
        temperament: data.data.result.temperament,
        acupuncturePoints: data.data.result.acupuncture_points,
        product: data.data.result.product,
      });

      setSelectedAxis(data.data.result.system);
    }
  }, [data, reset]);

  /* ================= GET BY ID ================= */
  const { data: editData } = useQuery({
    queryKey: ['systemAxisById', id],
    queryFn: () => getSystemAxisListById(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (editData?.data?.result) {
      const d = editData.data.result;
      reset({
        name: d.name,
        axisCode: d.axisCode,
        weightPriority: d.weightPriority,
        defaultElectricity: d.defaultElectricity,
        defaultPolarity: d.defaultPolarity,
        appArea: d.appArea,
        temperament: d.temperament,
        acupuncturePoints: d.acupuncture_points,
        note: d.note,
        product: d.product,
        active: d.active,
      });
      if (d.system) {
        setSelectedAxis(d.system);
      }
    }
  }, [editData, reset]);

  const createMutation = useCreateDiseaseProtocol({
    onSuccess: () => {
      reset();
      setSelectedAxis('');
      setLocalOptions([]);
      onSuccess?.();
    },
  });
  const updateMutation = useUpdateDiseaseProtocol({
    onSuccess: () => {
      reset();
      setSelectedAxis('');
      setLocalOptions([]);
      setSelectedId(null);
      onSuccess?.();
    },
  });

  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate({ id: id, ...formData });
      return;
    }

    createMutation.mutate(formData);
  };

  const isLoading =
    createMutation.isPending || updateMutation.isPending || isIdLoading;

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card
        w="100%"
        maxW="1200px"
        rounded="xl"
        boxShadow="rgba(0, 0, 0, 0.35) 0px 5px 15px"
      >
        <CardBody>
          <form onSubmit={handleSubmit(submitHandler)}>
            {/* ROW 1 */}
            <Flex gap={4} mb={10}>
              <FormControl isInvalid={!!errors.system}>
                <FormLabel>
                  System Name English{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <CreatableDropdown
                  borderColor={'#e3e7ea'}
                  isClearable={false}
                  options={systemOptions}
                  value={selectedAxis}
                  onChange={handleFilterChange}
                  onCreate={handleCreateSystem}
                  placeholder="Select or add system"
                  onMenuOpen={() => trigger('system')}
                />{' '}
                <input
                  type="hidden"
                  {...register('system', {
                    required: 'System is required',
                  })}
                />
                {errors.system && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.system.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.systemHi}>
                <FormLabel>
                  System Name Hindi{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  disabled={selectedAxisHiDisable}
                  placeholder="Enter System Name in Hindi"
                  {...register('systemHi', {
                    required: 'System Name in Hindi is required',
                  })}
                />
                {errors.systemHi && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.systemHi.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.diseaseEnglish}>
                <FormLabel>
                  Disease Name English{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter Disease Name in English"
                  {...register('diseaseEnglish', {
                    required: 'Disease Name in English is required',
                  })}
                />
                {errors.diseaseEnglish && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.diseaseEnglish.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.diseaseHindi}>
                <FormLabel>
                  Disease Name Hindi{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter Disease Name in Hindi"
                  {...register('diseaseHindi', {
                    required: 'Disease Name in Hindi is required',
                  })}
                />
                {errors.diseaseHindi && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.diseaseHindi.message}
                  </Text>
                )}
              </FormControl>
            </Flex>

            <Flex gap={4} mb={10}>
              <FormControl isInvalid={errors.bottle1PrimaryFunctional}>
                <FormLabel>
                  Bottle 1 Primary Functional{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter Bottle 1 Primary Functional"
                  {...register('bottle1PrimaryFunctional', {
                    required: 'Enter bottle 1 primary functional is required',
                  })}
                />
                {errors.bottle1PrimaryFunctional && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle1PrimaryFunctional.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.bottle1Supportive}>
                <FormLabel>
                  Bottle 1 Supportive{' '}
                  {/* <Text as="span" color="red.500">
                    *
                  </Text> */}
                </FormLabel>
                <Input
                  placeholder="Enter Bottle 1 Supportive"
                  {...register('bottle1Supportive', {
                    // required: 'Bottle 1 Supportive is required',
                  })}
                />
                {errors.bottle1Supportive && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle1Supportive.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.bottle1E}>
                <FormLabel>Bottle 1 Electricity</FormLabel>
                <Input
                  placeholder="Enter Bottle 1 Electricity"
                  {...register('bottle1E', {})}
                />
                {errors.bottle1E && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle1E.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex gap={4} mb={10}>
              <FormControl isInvalid={errors.bottle2PrimaryFunctional}>
                <FormLabel>
                  Bottle 2 Primary Functional{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter Bottle 2 Primary Functional"
                  {...register('bottle2PrimaryFunctional', {
                    required: 'Bottle 2 Primary Functional is required',
                  })}
                />
                {errors.bottle2PrimaryFunctional && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle2PrimaryFunctional.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle2Supportive}>
                <FormLabel>Bottle 2 supportive </FormLabel>
                <Input
                  placeholder="Enter Bottle 2 supportive"
                  {...register('bottle2Supportive', {})}
                />
                {errors.bottle2Supportive && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle2Supportive.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle2E}>
                <FormLabel>Bottle 2 Electricity</FormLabel>
                <Input
                  placeholder="Enter Bottle 2 Electricity"
                  {...register('bottle2E', {})}
                />
                {errors.bottle2E && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle2E.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex gap={4} mb={10}>
              <FormControl isInvalid={errors.bottle3Chronic}>
                <FormLabel>Bottle 3 Chronic</FormLabel>
                <Input
                  placeholder="Enter Bottle 3 Chronic"
                  {...register('bottle3Chronic', {})}
                />
                {errors.bottle3Chronic && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle3Chronic.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle3Supportive}>
                <FormLabel>Bottle 3 supportive</FormLabel>
                <Input
                  placeholder="Enter Bottle 3 supportive"
                  {...register('bottle3Supportive', {})}
                />
                {errors.bottle3Supportive && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle3Supportive.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle3E}>
                <FormLabel>Bottle 3 Electricity</FormLabel>
                <Input
                  placeholder="Enter Bottle 3 Electricity"
                  {...register('bottle3E', {})}
                />
                {errors.bottle3E && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle3E.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex mb={10} gap={2}>
              <FormControl isInvalid={errors.bottle4Degeneration}>
                <FormLabel>Bottle 4 Degeneration</FormLabel>
                <Input
                  placeholder="Enter Bottle 4 Degeneration"
                  {...register('bottle4Degeneration', {})}
                />
                {errors.bottle4Degeneration && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle4Degeneration.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle4Supportive}>
                <FormLabel>Bottle 4 supportive</FormLabel>
                <Input
                  placeholder="Enter Bottle 4 supportive"
                  {...register('bottle4Supportive', {})}
                />
                {errors.bottle4Supportive && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle4Supportive.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.bottle4E}>
                <FormLabel>Bottle 4 Electricity</FormLabel>
                <Input
                  placeholder="Enter Bottle 4 Electricity"
                  {...register('bottle4E', {})}
                />
                {errors.bottle4E && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.bottle4E.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex align="center" mb={10} gap={4}>
              <FormControl isInvalid={errors.state}>
                <FormLabel>Stage</FormLabel>
                <Input placeholder="Enter Stage" {...register('state', {})} />
                {errors.state && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.state.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.temperament}>
                <FormLabel>Constitution</FormLabel>
                <Input
                  placeholder="Enter Constitution"
                  {...register('temperament', {})}
                />
                {errors.temperament && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.temperament.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.acupuncturePoints}>
                <FormLabel>Acupuncture points</FormLabel>
                <Input
                  placeholder="Enter Acupuncture points"
                  {...register('acupuncturePoints', {})}
                />
                {errors.acupuncturePoints && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.acupuncturePoints.message}
                  </Text>
                )}
              </FormControl>
              <FormControl isInvalid={errors.product}>
                <FormLabel>Product Name</FormLabel>
                <Input
                  placeholder="Enter Product Name"
                  {...register('product', {})}
                />
                {errors.product && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.product.message}
                  </Text>
                )}
              </FormControl>
            </Flex>

            <Flex justify="center">
              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isLoading || isSubmitting}
                w="160px"
              >
                {id ? 'Update' : 'Submit'}
              </Button>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default DiseaseProtocolForm;
